import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { FormatGroup } from 'app/entity/models/entity';
import { Subscription } from 'rxjs/internal/Subscription';
import { ConfigurationLoader } from 'shared/configuration-loader';
import { IlsType } from 'shared/models/ilsType';
import { removeHoldItem, resetBulkHoldSubmitState, stopBulkHoldsProcessing, submitBulkHolds } from 'user/actions/bulk-hold.actions';
import { GetItPlaceTabHold, HoldNeededDateForm } from 'user/models/get-it';
import {
  BulkHoldState,
  BulkHoldSubmitState,
  getAvailableFormatGroups,
  getBulkHoldSubmitState,
  getUnavailableFormatGroups
} from 'user/reducers/bulk-hold.reducer';
import { LocationWithName, PickupArea } from '../../../../models/locations';
import { FeatureToggleService } from '../../../../services/feature-toggle.service';
import { TranslateService } from '@ngx-translate/core';
import { PickupLocationService } from 'user/services/pickup-location.service';

interface BulkHoldForm {
  pickupLocation: FormControl<string>;
  pickupArea: FormControl<string>;
  holdNeededDate: FormGroup<HoldNeededDateForm>,
}

@Component({
  selector: 'app-bulk-hold-modal',
  templateUrl: './bulk-hold-modal.component.html',
  styleUrls: ['./bulk-hold-modal.component.scss']
})
export class BulkHoldModalComponent implements OnInit {
  @Input() public pickupLocations: LocationWithName[];
  @Input() public selectedLocationCode: string;
  @Input() public preferredPickupLocationCode: string;
  @Input() public holdExpirationDefault: number;
  @Input() public holdActivationDateSetting: boolean;
  @Input() public userId: string;
  @Input() public selectedPickupArea: string;
  public availablePickupAreas: PickupArea[] = [];
  public unavailableFormatGroups$= this.store.select(getUnavailableFormatGroups);
  public availableFormatGroups: FormatGroup[];
  public bulkHoldForm: FormGroup<BulkHoldForm>;
  public bulkHoldSubmitState: BulkHoldSubmitState;
  private readonly subscription: Subscription = new Subscription();
  @ViewChild('cancelButton') public cancelButton: ElementRef;
  public selectedFormatGroups: FormatGroup[];
  public ilsType: IlsType;
  public showPickupAreaFeature = false;
  public pickupAreasLoading = false;

  constructor(
    private readonly activeModal: NgbActiveModal,
    private readonly store: Store<BulkHoldState>,
    private readonly fb: FormBuilder,
    private readonly configLoader: ConfigurationLoader,
    private readonly featureToggleService: FeatureToggleService,
    private readonly translateService: TranslateService,
    private readonly pickupLocationService: PickupLocationService,
  ) {
    this.store.dispatch(resetBulkHoldSubmitState());
  }

  public ngOnInit() {
    const isPickupAreaEnabled = this.featureToggleService.getToggles()['VEGAENG-179_2025_12_31_pickup_areas_updates'];
    this.bulkHoldForm = this.fb.group({
      pickupLocation: new FormControl(this.selectedLocationCode, Validators.required),
      pickupArea: new FormControl(null),
      holdNeededDate: this.fb.group<HoldNeededDateForm>({
        hasExpirationDate: this.fb.control<boolean>(false),
      }),
    });
    this.ilsType = this.configLoader.ilsType;
    this.showPickupAreaFeature = this.ilsType === IlsType.polaris && isPickupAreaEnabled;

    if (this.showPickupAreaFeature) {
      this.bulkHoldForm.get('pickupArea').setValidators(Validators.required);
      this.bulkHoldForm.get('pickupArea').updateValueAndValidity();
      this.loadPickupAreas(this.selectedLocationCode);
    }

    this.subscription.add(
      this.store.select(getAvailableFormatGroups).subscribe(formatGroups => {
        this.availableFormatGroups = formatGroups;

        this.selectedFormatGroups = this.availableFormatGroups.map(formatGroup => ({
          ...formatGroup,
          materialTabs: formatGroup.materialTabs ? [formatGroup.materialTabs[0]] : []
        }));

        if (formatGroups.length === 0) {
          this.setFocus();
        }
      })
    );

    this.subscription.add(
      this.store.select(getBulkHoldSubmitState).subscribe(bulkHoldSubmitState => {
        this.bulkHoldSubmitState = bulkHoldSubmitState;
        if (bulkHoldSubmitState.submitted) {
          this.bulkHoldForm.disable();
        }
      })
    );
  }

  public onFormatChange(updatedFormatGroups: FormatGroup[]) {
    this.selectedFormatGroups = updatedFormatGroups;
  }

  public isSafeToClose(): boolean {
    const {submitted, allProcessed, allStopped} = this.bulkHoldSubmitState;
    return !submitted || allProcessed || allStopped;
  }

  public isSubmitDisabled(): boolean {
    return !this.bulkHoldForm.valid || this.bulkHoldSubmitState.submitted || !this.availableFormatGroups.length || this.pickupAreasLoading;
  }

  public onClose(): void {
    if (this.isSafeToClose()) {
      this.activeModal.dismiss();
    }
  }

  public onStop(): void {
    this.store.dispatch(stopBulkHoldsProcessing());
  }

  public onSubmit() {
    if (this.isSubmitDisabled()) {
      return;
    }

    const holdRequests: GetItPlaceTabHold[] = this.selectedFormatGroups.map((formatGroup) => {
      return this.prepareHoldRequestObject(formatGroup);
    });

    this.store.dispatch(submitBulkHolds({holdRequests}));
  }

  public onRemoveItem(item: FormatGroup) {
    this.store.dispatch(removeHoldItem({item}));
  }

  public onChangePickupLocation(pickupLocationCode: string) {
    if (!this.showPickupAreaFeature) return;

    this.selectedPickupArea = null; // on location change we do not want area pre-selection
    this.loadPickupAreas(pickupLocationCode);
  }

  get hasPickupAreaError(): boolean {
    return this.availablePickupAreas.length && this.bulkHoldForm.get('pickupArea').invalid;
  }

  public loadPickupAreas(pickupLocationCode: string) {
    this.pickupAreasLoading = true;
    this.pickupLocationService.getUserPickupArea(pickupLocationCode).subscribe((areas: PickupArea[]) => {
      this.availablePickupAreas = areas;
      this.bulkHoldForm.get('pickupArea').reset();
      this.initializePickupAreaControl(this.availablePickupAreas);
      this.pickupAreasLoading = false;
    }, () => {
      this.pickupAreasLoading = false;
    });
  }

  public initializePickupAreaControl(areas: PickupArea[]): void {
    const pickupAreaControl = this.bulkHoldForm.get('pickupArea');
    if (!areas || !areas.length) {
      pickupAreaControl.clearValidators();
      pickupAreaControl.updateValueAndValidity();
      return;
    }

    pickupAreaControl.setValidators(Validators.required);
    pickupAreaControl.updateValueAndValidity();

    const defaultPickupArea = {
      code: '',
      description: this.translateService.instant('selectPickupAreaPlaceholder'),
      parentCode: '',
      selected: false,
    };

    if (this.selectedPickupArea) {
      pickupAreaControl.setValue(this.selectedPickupArea);
      return;
    }

    const noneOption = areas.find(a => a.description.toLowerCase().includes('none'));
    if (noneOption) {
      pickupAreaControl.setValue(noneOption.code);
    } else {
      areas.unshift(defaultPickupArea);
      pickupAreaControl.setValue(defaultPickupArea.code);
    }
  }

  public ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  private prepareHoldRequestObject(formatGroup: FormatGroup): GetItPlaceTabHold {
    const data: GetItPlaceTabHold = {
      patronId: this.userId,
      pickupLocation: this.bulkHoldForm.value.pickupLocation,
      pickupAreaId: this.bulkHoldForm.value.pickupArea,
      formatGroupId: formatGroup.id,
      materialTypeName: formatGroup.materialTabs[0].name,
      borrowByMail: false,
    };
    return this.addHoldExpirationDates(data);
  }

  private addHoldExpirationDates(data: GetItPlaceTabHold): GetItPlaceTabHold {
    const {neededBy, neededFrom, hasExpirationDate} = this.bulkHoldForm.value.holdNeededDate;
    if (neededBy) {
      data.neededBy = neededBy.toISOString();
    }
    if (neededFrom && hasExpirationDate) {
      data.neededFrom = neededFrom.toISOString();
    }
    return data;
  }

  private setFocus() {
    this.cancelButton?.nativeElement.focus();
  }
}
